import type { CSSProperties } from 'react';
import type { TripStatus } from '@dispatchdash/shared';
import { clientStatusLabel } from '@/lib/format';

const STATUS_COLORS: Record<string, string> = {
  requested: 'var(--color-status-pending)',
  confirmed: 'var(--color-status-active)',
  dispatched: 'var(--color-status-active)',
  executing: 'var(--color-status-active)',
  on_hold: 'var(--color-status-hold)',
  completed: 'var(--color-status-complete)',
  ready_to_bill: 'var(--color-status-complete)',
  billed: 'var(--color-status-complete)',
  closed: 'var(--color-charcoal-muted)',
  declined: 'var(--color-status-cancel)',
  cancelled: 'var(--color-status-cancel)',
  failed: 'var(--color-status-cancel)',
  pending: 'var(--color-status-pending)',
  arrived: 'var(--color-status-active)',
  servicing: 'var(--color-status-active)',
  serviced: 'var(--color-status-complete)',
  departed: 'var(--color-status-complete)',
  active: 'var(--color-status-active)',
  inactive: 'var(--color-charcoal-muted)',
  // Vehicle statuses
  available: 'var(--color-status-complete)',
  on_trip: 'var(--color-status-active)',
  maintenance: 'var(--color-status-hold)',
  unavailable: 'var(--color-charcoal-muted)',
  // App releases
  draft: 'var(--color-status-pending)',
  published: 'var(--color-status-complete)',
  withdrawn: 'var(--color-charcoal-muted)',
};

/** Wording that reads better than the title-cased status key. */
const STATUS_LABELS: Record<string, string> = {
  on_trip: 'On a trip',
  maintenance: 'In maintenance',
};

function formatStatus(status: string): string {
  return STATUS_LABELS[status] ?? status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface StatusBadgeProps {
  status: string;
  size?: 'sm' | 'md';
  /** 'client' uses customer-facing wording (e.g. billed → "Delivered") for the portal. */
  audience?: 'client';
  /** Override the displayed wording (colour still follows `status`). */
  label?: string;
}

export function StatusBadge({ status, size = 'md', audience, label }: StatusBadgeProps) {
  const color = STATUS_COLORS[status] ?? 'var(--color-charcoal-muted)';

  return (
    <span className={`status-badge status-${size}`} style={{ '--badge-color': color } as CSSProperties}>
      {label ?? (audience === 'client' ? clientStatusLabel(status) : formatStatus(status))}
      <style>{`
        .status-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 999px;
          font-weight: 500;
          background: color-mix(in srgb, var(--badge-color) 15%, transparent);
          color: var(--badge-color);
          border: 1px solid color-mix(in srgb, var(--badge-color) 30%, transparent);
        }
        .status-sm { font-size: 0.75rem; }
        .status-md { font-size: 0.8125rem; }
      `}</style>
    </span>
  );
}

export function TripStatusBadge({ status }: { status: TripStatus | string }) {
  return <StatusBadge status={status} />;
}
